import React, {useContext, useState} from "react";
import {useSelector} from "react-redux";
import {t} from "../../../../../store/localization/TranslMessasge";
import {IApplicationState} from "../../../../../store/Store";
import {IElementStateDto} from "../../../../../common/apis/Elements";
import {ElementDto} from "../../../../../common/apis/element/ElementDto";
import Dialog from "../../../../../components/dialogs/Dialog";
import DialogTitle from "../../../../../components/dialogs/DialogTitle";
import DialogContent from "../../../../../components/dialogs/DialogContent";
import DialogActions from "../../../../../components/dialogs/DialogActions";
import Grid from "../../../../../components/dialogs/Grid";
import TextField from "../../../../../components/fields/textfield/TextField";
import SingleSelectField from "../../../../../components/fields/select/SingleSelectField";
import {DatePicker} from "../../../../../components/fields/time/DatePicker";
import {SaveButton} from "../../../../../components/button/SaveButton";
import {CancelButton} from "../../../../../components/button/CancelButton";
import Snackbar from "../../snackbar/Snackbar";
import {CommonTranslationKey} from "../../CommonTranslationKey";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import {ElementDetailTranslationKey} from "../ElementDetailTranslationKey";
import {ElementTranslationKey} from "../../elements/ElementTranslationKey";
import ConfigurationContext, {Configuration} from "../../../../../common/ConfigurationContext";
import elementService from "../service/ElementService";
import {DateTimeFormatter} from "../../../../../common/DateTimeFormatter";
import GenerateDescriptionButton from "./GenerateDescriptionButton";
import ElementDescriptionGenerationDialog from "./ElementDescriptionGenerationDialog";

interface ElementBasicInfoUpdateDialogProps {
    open: boolean;
    element: ElementDto;
    onClose: () => void;
    onUpdate?: () => void;
}

export default function ElementBasicInfoUpdateDialog(props: Readonly<ElementBasicInfoUpdateDialogProps>) {
    const {open, element, onClose, onUpdate} = props;

    const states = useSelector((state: IApplicationState) => state.pages.common.options.states.resource || []);
    const {featureStatuses} = useContext<Configuration>(ConfigurationContext);

    const [name, setName] = useState<string>(element.name ?? "");
    const [description, setDescription] = useState<string>(element.description ?? "");
    const [selectedState, setSelectedState] = useState<IElementStateDto | null>(states.find(s => s.code === element.state) ?? null);
    const [validFrom, setValidFrom] = useState<Date | null>(element.validFrom ? new Date(element.validFrom) : null);
    const [validThru, setValidThru] = useState<Date | null>(element.validThru ? new Date(element.validThru) : null);
    const [nameError, setNameError] = useState<string | undefined>();
    const [inProgress, setInProgress] = useState(false);
    const [descGenDialogOpen, setDescGenDialogOpen] = useState(false);

    function validateName(): boolean {
        if (!name.trim()) {
            setNameError(t(CommonTranslationKey.FILL_OUT_THIS_FIELD));
            return false;
        }
        setNameError(undefined);
        return true;
    }

    function save() {
        if (!validateName()) return;

        setInProgress(true);
        elementService.updateBasicFields(element.identifier, {
            name: name.trim(),
            description: description ? description.trim() : null,
            state: selectedState?.code || null,
            validFrom: DateTimeFormatter.formatAsIsoDateTime(validFrom),
            validThru: DateTimeFormatter.formatAsIsoDateTime(validThru),
        })
                .then(() => {
                    Snackbar.success(t(CommonTranslationKey.DATA_SAVED_SUCCESSFULLY));
                    onUpdate?.();
                    onClose();
                })
                .catch((err) => {
                    Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), err);
                })
                .finally(() => setInProgress(false));
    }

    return (
            <>
                <Dialog open={open} onClose={onClose} maxWidth="md">
                    <DialogTitle title={t(ElementDetailTranslationKey.EDIT_ELEMENT_DETAILS)}
                            onDialogClosed={inProgress ? () => {
                            } : onClose}/>
                    <DialogContent>
                        <Grid container>
                            <Grid item xs={12}>
                                <TextField id="element-basic-info-edit-name"
                                        label={t(ElementTranslationKey.NAME)}
                                        value={name}
                                        onChange={(value) => setName(value)}
                                        errorMessage={nameError}
                                        required={true}/>
                            </Grid>
                            <Grid item xs={12} sx={{position: 'relative'}}>
                                <TextField id="element-basic-info-edit-description"
                                        label={t(ElementDetailTranslationKey.DESCRIPTION)}
                                        value={description}
                                        onChange={(value) => setDescription(value)}
                                        multiline={true}
                                        rows={8}/>
                                {featureStatuses.aiEnabled &&
                                        <GenerateDescriptionButton
                                                tooltip={t(ElementDetailTranslationKey.DESCRIPTION_GENERATE_USING_AI)}
                                                onClick={() => setDescGenDialogOpen(true)}
                                                inProgress={descGenDialogOpen}/>
                                }
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <SingleSelectField<IElementStateDto>
                                        id="element-basic-info-edit-state"
                                        label={t(ElementDetailTranslationKey.STATE)}
                                        value={selectedState}
                                        onChange={(value) => setSelectedState(value)}
                                        options={states}
                                        getOptionLabel={(option) => option.name}
                                        isOptionEqualToValue={(option, value) => option?.code === value?.code}/>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <DatePicker<Date>
                                        label={t(ElementDetailTranslationKey.VALID_FROM)}
                                        value={validFrom}
                                        onChange={setValidFrom}/>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <DatePicker<Date>
                                        label={t(ElementDetailTranslationKey.VALID_THRU)}
                                        value={validThru}
                                        onChange={setValidThru}/>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <CancelButton onClick={onClose} disabled={inProgress}/>
                        <SaveButton onClick={save} disabled={inProgress}/>
                    </DialogActions>
                    <ElementDescriptionGenerationDialog
                            open={descGenDialogOpen}
                            elementIdentifier={element.identifier}
                            onClosed={() => setDescGenDialogOpen(false)}
                            onDescriptionGenerated={setDescription}
                    />
                </Dialog>
            </>
    );
}
